import { useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { User, Mail, Phone, Calendar, Settings, History, Heart } from 'lucide-react';

export const AccountPage = () => {
  const { t } = useI18n();
  useDocumentLang();

  const [activeTab, setActiveTab] = useState<'profile' | 'reservations' | 'preferences'>('profile');

  const [profileData, setProfileData] = useState({
    name: 'John Doe',
    email: 'john@example.com',
    phone: '+1 (555) 123-4567',
  });

  const [preferences, setPreferences] = useState({
    dietaryRestrictions: '',
    allergies: '',
    favoriteSeating: '',
    specialRequests: '',
  });

  // Mock reservation data
  const mockReservations = [
    {
      id: '1',
      restaurant: 'Sample Restaurant A',
      date: '2025-01-20',
      time: '19:00',
      guests: 4,
      status: 'confirmed',
    },
    {
      id: '2',
      restaurant: 'Sample Restaurant B',
      date: '2025-01-15',
      time: '18:30',
      guests: 2,
      status: 'completed',
    },
  ];

  const handleProfileSave = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saving profile:', profileData);
    // TODO: Implement save to API
  };

  const handlePreferencesSave = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saving preferences:', preferences);
    // TODO: Implement save to API
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-8 text-white">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                <User className="h-10 w-10" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">{profileData.name}</h1>
                <p className="text-blue-100">{profileData.email}</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex gap-8 px-8">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                  activeTab === 'profile'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Settings className="inline-block h-4 w-4 mr-2" />
                {t('account.tabs.profile')}
              </button>
              <button
                onClick={() => setActiveTab('reservations')}
                className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                  activeTab === 'reservations'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <History className="inline-block h-4 w-4 mr-2" />
                {t('account.tabs.reservations')}
              </button>
              <button
                onClick={() => setActiveTab('preferences')}
                className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                  activeTab === 'preferences'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Heart className="inline-block h-4 w-4 mr-2" />
                {t('account.tabs.preferences')}
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileSave} className="space-y-6 max-w-2xl">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                  {t('account.profile.title')}
                </h2>

                <div>
                  <Label htmlFor="name">{t('account.profile.name')}</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {t('account.profile.email')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    {t('account.profile.phone')}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                >
                  {t('account.profile.saveButton')}
                </Button>
              </form>
            )}

            {/* Reservations Tab */}
            {activeTab === 'reservations' && (
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                  {t('account.reservations.title')}
                </h2>

                <div className="space-y-4">
                  {mockReservations.map((reservation) => (
                    <div
                      key={reservation.id}
                      className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {reservation.restaurant}
                          </h3>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              {reservation.date} at {reservation.time}
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4" />
                              {reservation.guests} {t('account.reservations.guests')}
                            </div>
                          </div>
                        </div>
                        <div>
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              reservation.status === 'confirmed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {t(`account.reservations.status.${reservation.status}`)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {mockReservations.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      {t('account.reservations.noReservations')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <form onSubmit={handlePreferencesSave} className="space-y-6 max-w-2xl">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                  {t('account.preferences.title')}
                </h2>

                <div>
                  <Label htmlFor="dietary">{t('account.preferences.dietary')}</Label>
                  <Textarea
                    id="dietary"
                    value={preferences.dietaryRestrictions}
                    onChange={(e) => setPreferences({ ...preferences, dietaryRestrictions: e.target.value })}
                    placeholder={t('account.preferences.dietaryPlaceholder')}
                    rows={3}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="allergies">{t('account.preferences.allergies')}</Label>
                  <Textarea
                    id="allergies"
                    value={preferences.allergies}
                    onChange={(e) => setPreferences({ ...preferences, allergies: e.target.value })}
                    placeholder={t('account.preferences.allergiesPlaceholder')}
                    rows={3}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="seating">{t('account.preferences.seating')}</Label>
                  <Input
                    id="seating"
                    value={preferences.favoriteSeating}
                    onChange={(e) => setPreferences({ ...preferences, favoriteSeating: e.target.value })}
                    placeholder={t('account.preferences.seatingPlaceholder')}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="special">{t('account.preferences.special')}</Label>
                  <Textarea
                    id="special"
                    value={preferences.specialRequests}
                    onChange={(e) => setPreferences({ ...preferences, specialRequests: e.target.value })}
                    placeholder={t('account.preferences.specialPlaceholder')}
                    rows={3}
                    className="mt-2"
                  />
                </div>

                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                >
                  {t('account.preferences.saveButton')}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
